import phoneFormatter from 'phone-formatter';
import formatStatus from './formatStatus';
import formatAmount from './formatAmount';
import { formatDate } from './formatDate';
import {
	determineStripeFee,
	determineNet,
	determineRefund,
} from './determineFees';
import determinePaidAt from './determinePaidAt';

const getRunnerAmountRefundedCents = (invoice) => {
	if (
		typeof invoice.refundedAmount === 'number' &&
		invoice.refundedAmount > 0
	) {
		return invoice.refundedAmount;
	}

	const fromStripeCharges = determineRefund(invoice);
	if (fromStripeCharges) {
		return fromStripeCharges;
	}

	const status = (invoice.status || invoice.statusFormatted || '')
		.toString()
		.toLowerCase();

	if (status === 'refunded') {
		return invoice.total ?? invoice.amount ?? null;
	}

	if (
		status === 'partially refunded' ||
		status === 'partial refund'
	) {
		return null;
	}

	return null;
};

const formatPhone = ({ phone }) => {
	if (phone) {
		return phoneFormatter.format(phone, 'NNN NNN NNNN');
	}
	return '';
};

// This function will take all the properties we are given from stripe and keeping only the ones we need
// It also will also add some formatted properties that we will use

const formatInvoices = ({ invoices }) => {
	return invoices.map((invoice) => {
		// Check if this runner invoice has a Stripe payment intent ID
		if (invoice.type === 'runner_invoice' && !invoice.paymentIntentId) {
			// Runner invoice without Stripe payment intent - set charges to null to prevent crashes
			return {
				id: invoice._id,
				_invoice: invoice._id || invoice._invoice,
				_project: invoice?._project,
				amount: invoice.total,
				amountFormatted: formatAmount({ amount: invoice.total }),
				amountFormattedWithCurrency: formatAmount({
					amount: invoice.total,
					currency: invoice.currency,
				}),
				created: invoice.createdAt,
				createdFormatted: formatDate({
					date: invoice.createdAt,
					platform: 'runner',
				}),
				createdFormattedShort: formatDate({
					date: invoice.createdAt,
					platform: 'runner',
					short: true,
				}),
				paidAt: determinePaidAt(invoice),
				currency: invoice.currency || 'usd',
				description: invoice.description || invoice.name || '',
				metadata: {},
				status: invoice.status,
				statusFormatted: invoice.status,
				customer: {
					_id: invoice._customer?._id || invoice._customer,
					name: invoice.customer?.name || '',
					phone: invoice.customer?.phone || '',
					phoneFormatted: formatPhone({
						phone: invoice.customer?.phone,
					}),
					email: invoice.customer?.email || '',
				},
				paymentTerms: invoice.paymentTerms || '',
				number: invoice.number || '',
				type: 'runner_invoice',
				lineItems: invoice.lineItems || [],
				discountPercent: invoice.discountPercent || 0,
				discountAmount: invoice.discountAmount || 0,
				taxLines: invoice.taxLines || [],
				total: invoice.total,
				refundedAmount: invoice.refundedAmount,
				refundedAt: invoice.refundedAt,
				lastRefundedAt: invoice.lastRefundedAt,
				amountRefunded: getRunnerAmountRefundedCents(invoice),
				// Set charges to null to prevent crashes when determineRefund tries to access it
				charges: null,
				hasStripePaymentIntent: false,
			};
		} else if (
			invoice.type === 'runner_invoice' &&
			invoice.paymentIntentId
		) {
			// Runner invoice with Stripe payment intent ID - should have charges data
			return {
				id: invoice.id, // This is the Stripe payment intent ID
				paymentIntentId: invoice.paymentIntentId,
				_invoice: invoice._id || invoice._invoice,
				_project: invoice?._project,
				amount: invoice.amount || invoice.total,
				amountFormatted: formatAmount({
					amount: invoice.amount || invoice.total,
				}),
				amountFormattedWithCurrency: formatAmount({
					amount: invoice.amount || invoice.total,
					currency: invoice.currency,
				}),
				created: invoice.created || invoice.createdAt,
				createdFormatted: formatDate({
					date: invoice.createdAt,
					platform: 'runner',
				}),
				createdFormattedShort: formatDate({
					date: invoice.createdAt,
					platform: 'runner',
					short: true,
				}),
				paidAt: determinePaidAt(invoice),
				currency: invoice.currency || 'usd',
				description: invoice.description || invoice.name || '',
				metadata: invoice.metadata || {},
				status: invoice.status || invoice.statusFormatted,
				statusFormatted: invoice.status || invoice.statusFormatted,
				customer: {
					_id: invoice._customer?._id || invoice._customer,
					id: invoice.customer?.id,
					email: invoice.customer?.email,
					name: invoice.customer?.name || '',
					phone: invoice.customer?.phone,
					phoneFormatted: formatPhone({
						phone: invoice.customer?.phone,
					}),
				},
				// Include Stripe charges data so determineRefund can safely access it
				charges: invoice.charges,
				type: 'runner_invoice',
				lineItems: invoice.lineItems || [],
				discountPercent: invoice.discountPercent || 0,
				discountAmount: invoice.discountAmount || 0,
				taxLines: invoice.taxLines || [],
				total: invoice.total,
				refundedAmount: invoice.refundedAmount,
				refundedAt: invoice.refundedAt,
				lastRefundedAt: invoice.lastRefundedAt,
				amountRefunded: getRunnerAmountRefundedCents(invoice),
			};
		}

		// Default: Stripe/regular invoice logic
		const result = {
			id: invoice.id,
			paymentIntentId: invoice?.id,
			_invoice: invoice?.invoice?._id || invoice?._invoice,
			_runnerInvoice: invoice?._runnerInvoice,
			_project: invoice?._project,
			invoice: invoice.invoice,
			type: invoice.type || 'stripe_invoice',
			amount: invoice.amount,
			amountFormatted: formatAmount({ amount: invoice.amount }),
			amountFormattedWithCurrency: formatAmount({
				amount: invoice.amount,
				currency: invoice.currency,
			}),
			applicationFeeAmount: invoice.application_fee_amount,
			applicationFeeAmountFormatted: formatAmount({
				amount: invoice.application_fee_amount,
				currency: invoice.currency,
			}),
			customer: {
				_id: invoice._customer || invoice.invoice?._customer,
				id: invoice.customer && invoice.customer.id,
				email: invoice.customer && invoice.customer.email,
				name: invoice.customer && invoice.customer.name,
				phone: invoice.customer && invoice.customer.phone,
				phoneFormatted: formatPhone({
					phone: invoice.customer && invoice.customer.phone,
				}),
			},
			charges: invoice.charges,
			/** Expanded Charge; use when charges.data is empty (common with newer Stripe PI shapes). */
			latestCharge: invoice.latest_charge,
			created: invoice.created,
			createdFormatted: formatDate({ date: invoice.created }),
			createdFormattedShort: formatDate({
				date: invoice.created,
				short: true,
			}),
			paidAt: determinePaidAt(invoice),
			currency: invoice.currency,
			description: invoice.description,
			metadata: invoice.metadata,
			net: determineNet(invoice),
			netFormatted: formatAmount({
				amount: determineNet(invoice),
				currency: invoice.currency,
			}),
			paymentMethod: invoice.payment_method,
			status: invoice.status,
			statusFormatted: formatStatus({
				status: invoice.status,
				charges: invoice.charges,
				latestCharge: invoice.latest_charge,
			}),
			stripeFee: formatAmount({
				amount: determineStripeFee(invoice),
				currency: invoice.currency,
			}),
			// Calculate refunded amount for refunded invoices
			amountRefunded: (() => {
				const status = formatStatus({
					status: invoice.status,
					charges: invoice.charges,
					latestCharge: invoice.latest_charge,
				});
				if (status === 'Refunded' || status === 'Partial Refund') {
					const netAmount = determineNet(invoice);
					return invoice.amount - netAmount;
				}
				return null;
			})(),
		};

		return result;
	});
};

export default formatInvoices;
